import { useTranslation } from "react-i18next";
import { InfoCircleTwoTone } from "@ant-design/icons";
import { type FormInstance } from "antd";
import { createStyles } from "antd-style";

import { Checkbox } from "@av/components/atoms/Checkbox";
import { Form } from "@av/components/atoms/form/Form";
import { Input } from "@av/components/atoms/input/Input";
import { Modal } from "@av/components/atoms/Modal";
import { Select } from "@av/components/atoms/Select";
import { TooltipLabel } from "@av/components/molecules/TooltipLabel";
import { mapErrors } from "../../../../services/config/extract-validation-errors";

import type { ApiError, SelectOption } from "../../../../services/admin-settings/type";

export type CreateUserFormValues = {
  email?: string;
  displayName?: string;
  securityGroupIds?: string[];
  emailUser?: boolean;
};

type CreateUserModalProps = {
  form: FormInstance<CreateUserFormValues>;
  onCancel: () => void;
  onSubmit: (values: CreateUserFormValues) => void;
  open: boolean;
  securityGroupOptions: SelectOption[] | undefined;
  serverError: ApiError | null;
  isSubmitting: boolean;
  /** Shows the Email user checkbox, ticked by default. Only while ATS_USERS_EMAIL is on. */
  showEmailUser?: boolean;
};

const useStyles = createStyles(({ css, token }) => ({
  form: css`
    .ant-form-item {
      margin-bottom: ${token.marginSM}px;
    }

    .ant-form-item-row {
      align-items: center;
    }

    .ant-form-item-label {
      padding-bottom: 0;
      text-align: left;
    }

    .ant-form-item-label > label {
      height: auto;
      color: ${token.colorText};
      font-size: ${token.fontSize}px;
    }
  `,
}));

export function CreateUserModal(props: CreateUserModalProps) {
  const {
    form,
    isSubmitting,
    onCancel,
    onSubmit,
    open,
    serverError,
    securityGroupOptions,
    showEmailUser = false,
  } = props;
  const { t } = useTranslation();
  const { styles } = useStyles();

  return (
    <Modal
      testIdPrefix="ats-admin-settings-create-user"
      title={t("platform.adminSettings.users_dialog_addUser_title")}
      open={open}
      onCancel={onCancel}
      onOk={() => form.submit()}
      afterClose={() => form.resetFields()}
      okText={t("platform.adminSettings.users_dialog_addUser_submit")}
      okButtonProps={{ loading: isSubmitting }}
      cancelButtonProps={{ style: { display: "none" } }}
      // Can't be closed while adding or emailing the user, so a reopened dialog is never closed by the earlier submit
      closable={!isSubmitting}
      maskClosable={!isSubmitting}
      keyboard={!isSubmitting}
      forceRender
      width={610}
    >
      <Form
        serverErrors={mapErrors(t, serverError)}
        form={form}
        labelCol={{ flex: "128px" }}
        wrapperCol={{ flex: 1 }}
        layout="horizontal"
        requiredMark={false}
        onFinish={onSubmit}
        className={styles.form}
      >
        <Form.Item
          testIdPrefix="ats-admin-settings-create-user-name"
          label={t("platform.adminSettings.users_dialog_addUser_name")}
          name="displayName"
        >
          <Input
            testIdPrefix="ats-admin-settings-create-user-name-input"
            placeholder={t("platform.adminSettings.users_dialog_addUser_placeholder")}
          />
        </Form.Item>
        <Form.Item
          testIdPrefix="ats-admin-settings-create-user-email"
          labelString={t("platform.adminSettings.users_dialog_addUser_email")}
          label={
            <TooltipLabel
              testIdPrefix="ats-admin-settings-create-user-email"
              label={t("platform.adminSettings.users_dialog_addUser_email")}
              title={
                <>
                  <strong>
                    {t("platform.adminSettings.users_dialog_addUser_emailTooltip_title")}
                  </strong>
                  <br />
                  {t("platform.adminSettings.users_dialog_addUser_emailTooltip_content")}
                </>
              }
              icon={<InfoCircleTwoTone />}
            />
          }
          name="email"
        >
          <Input
            testIdPrefix="ats-admin-settings-create-user-email-input"
            placeholder={t("platform.adminSettings.users_dialog_addUser_placeholder")}
          />
        </Form.Item>
        <Form.Item
          testIdPrefix="user-settings-assign-user-security-group"
          label={t("platform.adminSettings.users_dialog_securityGroup")}
          name="securityGroupIds"
        >
          <Select
            allowClear
            showSearch
            optionFilterProp="label"
            testIdPrefix="user-settings-assign-user-security-groups-security-groups"
            mode="multiple"
            options={securityGroupOptions}
          />
        </Form.Item>
        {showEmailUser && (
          <Form.Item
            testIdPrefix="ats-admin-settings-create-user-email-user"
            // Names the checkbox for screen readers; without it Form.Item falls back to the field name ("emailUser")
            labelString={t("platform.adminSettings.users_dialog_addUser_emailUser")}
            name="emailUser"
            valuePropName="checked"
            initialValue={true}
          >
            <Checkbox testIdPrefix="ats-admin-settings-create-user-email-user">
              {t("platform.adminSettings.users_dialog_addUser_emailUser")}
            </Checkbox>
          </Form.Item>
        )}
      </Form>
    </Modal>
  );
}
